import React, { useState } from 'react';
import { ArrowUpTrayIcon, DocumentTextIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useData } from '../../context/DataContext';
import dayjs from 'dayjs';

const ImportTransactions = () => {
  const { portfolios, importTransactions } = useData();
  const [previewData, setPreviewData] = useState([]);
  const [importedCount, setImportedCount] = useState(0);
  const [feedbackMessage, setFeedbackMessage] = useState(null);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const content = e.target.result;
        let data;

        if (file.name.endsWith('.json')) {
          // Handle JSON import
          data = JSON.parse(content);
          processTransactions(data);
        } else if (file.name.endsWith('.csv')) {
          // Handle CSV import
          data = parseCSV(content);
          processTransactions(data);
        } else {
          setFeedbackMessage({ type: 'error', text: 'Unsupported file format. Please upload JSON or CSV.' });
        }
      } catch (error) {
        console.error('Import error:', error);
        setFeedbackMessage({ type: 'error', text: 'Failed to parse file. Please check the format.' });
      }
    };

    reader.readAsText(file);
    event.target.value = '';
  };

  const parseCSV = (csvContent) => {
    const lines = csvContent.trim().split('\n');
    const headers = lines[0].split(',').map(h => h.trim());

    const transactions = lines.slice(1).map((line, index) => {
      const values = line.split(',').map(v => v.trim());
      const txn = {};

      headers.forEach((header, i) => {
        txn[header.toLowerCase()] = values[i];
      });

      // Generate ID if not present
      if (!txn.id) {
        txn.id = `txn-${Date.now()}-${index}`;
      }

      // Parse amount as number
      if (txn.amount) {
        txn.amount = parseFloat(txn.amount);
      }

      // Ensure date is in correct format
      if (txn.date) {
        txn.date = dayjs(txn.date, ['DD/MM/YYYY', 'YYYY-MM-DD', 'MM/DD/YYYY']).format('YYYY-MM-DD');
      }

      return txn;
    });

    return transactions;
  };

  const processTransactions = (transactions) => {
    // Validate transactions
    const validTransactions = transactions.filter(txn => {
      return txn.portfolioId && txn.amount && txn.date && txn.type;
    });

    if (validTransactions.length === 0) {
      setFeedbackMessage({ type: 'error', text: 'No valid transactions found in file.' });
      return;
    }

    // Check if portfolios exist
    const validatedTransactions = validTransactions.map(txn => {
      const portfolio = portfolios.find(p => p.id === txn.portfolioId);
      return {
        ...txn,
        portfolioExists: !!portfolio,
        portfolioName: portfolio?.name || 'Unknown Portfolio'
      };
    });

    setPreviewData(validatedTransactions);
    setFeedbackMessage(null);
  };

  const handleImport = () => {
    const validTransactions = previewData.filter(txn => txn.portfolioExists);

    if (validTransactions.length === 0) {
      setFeedbackMessage({ type: 'error', text: 'No valid transactions to import.' });
      return;
    }

    // Remove validation fields before import
    const cleanTransactions = validTransactions.map(({ portfolioExists, portfolioName, ...txn }) => txn);

    importTransactions(cleanTransactions);
    setImportedCount(cleanTransactions.length);
    setFeedbackMessage({ type: 'success', text: `Successfully imported ${cleanTransactions.length} transaction(s)!` });

    // Clear preview after a delay
    setTimeout(() => {
      setPreviewData([]);
      setImportedCount(0);
      setFeedbackMessage(null);
    }, 3000);
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'contribution': return 'green';
      case 'withdrawal': return 'red';
      case 'dividend': return 'blue';
      default: return 'gray';
    }
  };

  const downloadTemplate = (format) => {
    const template = format === 'csv'
      ? 'date,portfolioId,type,amount,description,category\n2024-11-16,portfolio-123,contribution,1000.00,Monthly contribution,regular\n2024-11-16,portfolio-123,dividend,50.00,Dividend payment,income'
      : JSON.stringify([
          {
            id: "txn-example-1",
            date: "2024-11-16",
            type: "contribution",
            amount: 1000.00,
            portfolioId: "portfolio-123",
            description: "Monthly contribution",
            category: "regular"
          },
          {
            id: "txn-example-2",
            date: "2024-11-16",
            type: "dividend",
            amount: 50.00,
            portfolioId: "portfolio-123",
            description: "Dividend payment",
            category: "income"
          }
        ], null, 2);

    const blob = new Blob([template], { type: format === 'csv' ? 'text/csv' : 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transaction-template.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 m-0">
          Import Transactions
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Import transactions from CSV or JSON files
        </p>
      </div>

      <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex flex-col gap-6">
          <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
            <div className="flex flex-col gap-2">
              <p className="font-semibold text-gray-900">Supported Formats</p>
              <div>
                <p className="mb-2 block text-gray-700">
                  <strong>CSV Format:</strong> date, portfolioId, type, amount, description, category
                </p>
                <p className="mb-2 block text-gray-700">
                  <strong>JSON Format:</strong> Array of transaction objects with the same fields
                </p>
                <p className="text-gray-700">
                  <strong>Transaction Types:</strong> contribution, withdrawal, dividend, fee
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => downloadTemplate('csv')}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <DocumentTextIcon className="w-5 h-5" />
              <span>Download CSV Template</span>
            </button>
            <button
              onClick={() => downloadTemplate('json')}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <DocumentTextIcon className="w-5 h-5" />
              <span>Download JSON Template</span>
            </button>
          </div>

          <div className="bg-gray-100 rounded-lg border-2 border-dashed border-gray-400 p-12 text-center cursor-pointer">
            <input
              type="file"
              accept=".csv,.json"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              id="upload-file-input"
            />
            <div
              className="flex flex-col gap-2 items-center justify-center cursor-pointer"
              onClick={() => document.getElementById('upload-file-input').click()}
            >
              <ArrowUpTrayIcon className="w-20 h-20 text-blue-600" />
              <p className="text-base font-medium cursor-pointer text-gray-900">
                Click or drag file to this area to upload
              </p>
              <p className="text-sm text-gray-500">
                Support for CSV and JSON transaction files
              </p>
            </div>
          </div>
        </div>
      </div>

      {feedbackMessage && (
        <div className={`rounded-lg border p-4 mb-6 ${
          feedbackMessage.type === 'success' ? 'bg-green-50 border-green-200' :
          feedbackMessage.type === 'error' ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'
        }`}>
          <p className="text-gray-900">{feedbackMessage.text}</p>
        </div>
      )}

      {previewData.length > 0 && (
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 mb-6">
          <div className="flex flex-col gap-4">
            <div className="flex flex-row justify-between items-center">
              <h3 className="text-base font-semibold text-gray-900">
                Preview Transactions ({previewData.length})
              </h3>
              <button
                onClick={handleImport}
                disabled={previewData.filter(t => t.portfolioExists).length === 0}
                className="px-4 py-2  text-white rounded-lg  transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: '#012E58' }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
              >
                Import {previewData.filter(t => t.portfolioExists).length} Transaction(s)
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-300">
                    <th className="p-3 text-left font-semibold">Date</th>
                    <th className="p-3 text-left font-semibold">Portfolio</th>
                    <th className="p-3 text-left font-semibold">Type</th>
                    <th className="p-3 text-right font-semibold">Amount</th>
                    <th className="p-3 text-left font-semibold">Description</th>
                    <th className="p-3 text-center font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((transaction) => (
                    <tr key={transaction.id} className="border-b border-gray-200">
                      <td className="p-3 text-gray-900">
                        {dayjs(transaction.date).format('DD/MM/YYYY')}
                      </td>
                      <td className="p-3">
                        <div className="flex flex-row gap-2 items-center">
                          <span className={transaction.portfolioExists ? 'text-gray-900' : 'text-red-600'}>
                            {transaction.portfolioName}
                          </span>
                          {!transaction.portfolioExists && (
                            <span className="inline-block bg-red-600 rounded px-2 py-1 text-xs text-white">
                              Not Found
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        <span className={`inline-block rounded px-2 py-1 text-xs capitalize ${
                          getTypeColor(transaction.type) === 'green' ? 'bg-green-100 text-green-800' :
                          getTypeColor(transaction.type) === 'red' ? 'bg-red-100 text-red-800' :
                          getTypeColor(transaction.type) === 'blue' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {transaction.type}
                        </span>
                      </td>
                      <td className="p-3 text-right font-semibold font-mono">
                        £{transaction.amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="p-3 text-xs text-gray-600">
                        {transaction.description}
                      </td>
                      <td className="p-3 text-center">
                        {transaction.portfolioExists ? (
                          <div className="flex flex-row gap-1 items-center justify-center">
                            <CheckCircleIcon className="w-4 h-4 text-green-600" />
                            <span className="inline-block bg-green-100 text-green-800 rounded px-2 py-1 text-xs">
                              Ready
                            </span>
                          </div>
                        ) : (
                          <span className="inline-block bg-red-600 rounded px-2 py-1 text-xs text-white">
                            Invalid
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {importedCount > 0 && (
        <div className="bg-green-50 rounded-lg border border-green-200 p-4">
          <p className="text-gray-900">{`Successfully imported ${importedCount} transaction(s)!`}</p>
        </div>
      )}
    </div>
  );
};

export default ImportTransactions;
